import { ChatMessage } from './chat-message';
import { ChatModel } from './chat-model';
import { MessageType } from './message-type';
import { UserUtil } from '@contact/common-util-types';

export class ChatTab {
  public newMessagesCount: number;
  public closedByClient: boolean;
  public conversationEndByClient: boolean;
  public lastMessageTime: number;
  public chatName: string;

  public timerForLastMessage: number = -1;
  public durationExceededCountdownTimer: number = -1;
  public slaTimer: number = -1;

  public postTreatmentTimerValue: number = -1;
  public postTreatmentTimerReprieveValue: number = -1;

  private _firstMessageTime: number;
  private _lastMessage: string;
  private _isLastMessageFromClient: boolean;

  private LAST_MESSAGE_TIMER = 'LAST_MESSAGE_TIMER';
  private DURATION_EXCEEDED_COUNTDOWN_TIMER = 'DURATION_EXCEEDED_COUNTDOWN_TIMER';

  constructor(chatName: string, lastMessage: string, _newMessagesCount: number) {
    this.chatName = chatName;
    this.newMessagesCount = _newMessagesCount;
    this._lastMessage = lastMessage;
    this._firstMessageTime = Number.MAX_VALUE;
    this.closedByClient = false;
    this.conversationEndByClient = false;
    this._isLastMessageFromClient = false;
  }

  get firstMessageTime(): number {
    return this._firstMessageTime;
  }

  get lastMessage(): string {
    return this._lastMessage;
  }

  get isLastMessageFromClient(): boolean {
    return this._isLastMessageFromClient;
  }

  static fromChat(chatModel: ChatModel): ChatTab {
    let chatName: string = '';
    let lastMessageFromClient: string = '';
    let unread = 0;
    if (chatModel.messages.length > 0) {
      // set chat name from messages data
      const msgFromCustomer = chatModel.messages.find(m => m.isFromCustomer());
      if (!!msgFromCustomer) {
        chatName = ChatTab.computeChatNameFromMessage(msgFromCustomer);
      }

      // set unread count
      let index = chatModel.messages.length - 1;
      while (index >= 0 && chatModel.messages[index].isFromCustomer()) {
        index--;
      }
      unread = chatModel.messages.length - 1 - index;
    }

    const chatTab = new ChatTab(chatName, lastMessageFromClient, unread);

    // Update last message and elapsedTime
    chatModel.messages
      .filter(message => message.type === MessageType.CHAT || message.type === MessageType.CHAT_AUTOMATIC_MESSAGE)
      .forEach(m => {
        chatTab.updateChatTabInfo(m);
      });

    return chatTab;
  }

  static computeChatNameFromMessage(msg: ChatMessage): string {
    return this.computeChatName(msg.firstName, msg.lastName);
  }

  static computeChatName(firstName: string, lastName: string): string {
    return (
      UserUtil.fullname({
        firstName,
        lastName,
      }) ?? UserUtil.UNKNOWN
    );
  }

  hasNewMessage(): boolean {
    return this.newMessagesCount > 0;
  }

  increaseUnreadCount() {
    this.newMessagesCount++;
  }

  resetUnreadCount() {
    this.newMessagesCount = 0;
  }

  updateChatTabInfo(message: ChatMessage) {
    const previousMessageWasFromClient = this._isLastMessageFromClient;
    this._isLastMessageFromClient = ChatTab.lastMessageSendByVisitor(message, previousMessageWasFromClient);
    if (this._isLastMessageFromClient && message.isFromCustomer()) {
      if (message.type === MessageType.END) {
        this.conversationEndByClient = true;
        return;
      }
      if (message.type === MessageType.LEAVE) {
        this.closedByClient = true;
      } else if (!ChatTab.isScreenSharingMessage(message)) {
        if (this.closedByClient) {
          this.closedByClient = false;
          this.lastMessageTime = message.date;
        }
        this._lastMessage = message.content;
        if (!previousMessageWasFromClient) {
          this.lastMessageTime = message.date;
        }
        if (message.date < this._firstMessageTime) {
          this._firstMessageTime = message.date;
        }
      }
    }
  }

  private static lastMessageSendByVisitor(message: ChatMessage, previousMessageWasFromClient: boolean): boolean {
    return message.isFromCustomer() || (previousMessageWasFromClient && ChatTab.isSystemMessageLike(message.type));
  }

  private static isSystemMessageLike(type: MessageType) {
    return (
      [
        MessageType.CONFERENCE,
        MessageType.END_CONFERENCE,
        MessageType.TRANSFER,
        MessageType.TRANSFER_COMMENT,
        MessageType.DEMAND_SCREEN_SHARING,
        MessageType.REFUSE_SCREEN_SHARING,
        MessageType.CANCEL_SCREEN_SHARING,
        MessageType.ACCEPT_SCREEN_SHARING,
        MessageType.ACCEPTED_SCREEN_SHARING,
        MessageType.END_SCREEN_SHARING,
        MessageType.FAILURE_SCREEN_SHARING,
      ].indexOf(type) >= 0
    );
  }

  private static isScreenSharingMessage(message: ChatMessage): boolean {
    return (
      [
        MessageType.DEMAND_SCREEN_SHARING,
        MessageType.REFUSE_SCREEN_SHARING,
        MessageType.CANCEL_SCREEN_SHARING,
        MessageType.ACCEPT_SCREEN_SHARING,
        MessageType.ACCEPTED_SCREEN_SHARING,
        MessageType.END_SCREEN_SHARING,
        MessageType.FAILURE_SCREEN_SHARING,
      ].indexOf(message.type) >= 0
    );
  }

  getLastMessageTimerName(customerId: string) {
    return this.LAST_MESSAGE_TIMER + '_' + customerId;
  }

  getDurationExceededTimerName(customerId: string) {
    return this.DURATION_EXCEEDED_COUNTDOWN_TIMER + '_' + customerId;
  }
}
